/**
 * InviteStatusCard.tsx — Card compacto de status de convite/ativação
 *
 * Usado dentro de fichas/detalhes de usuário para mostrar o status atual
 * do convite, tempo restante, e ações (Recuperar Acesso, Desativar/Reativar, WhatsApp).
 *
 * @internal true
 * @owner components/invite
 * @updated 2026-08-16
 */
import { useState } from 'react';
import {
  CheckCircle2, Clock, AlertTriangle, Copy, Check,
  RefreshCw, Share2, HelpCircle, UserX, UserCheck, KeyRound
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { ROUTES } from '../../api/routes';
import {
  getInviteStatus,
  getTempoRestante,
  formatDate,
  buildActivationLink,
  normalizeActivationLink,
  buildWhatsAppUrl,
  copyActivationLink,
  type InviteUser,
  type InviteStatusType,
} from './invite-utils';
import { InviteResultDialog } from './InviteResultDialog';
import './invite.css';

interface InviteStatusCardProps {
  /** Dados do usuário */
  user: InviteUser;
  /** Callback ao clicar "Gerar Novo Link" — recebe userId */
  onResend?: (userId: string) => Promise<void> | void;
  /** Callback ao alterar status do usuário (ativar/desativar) */
  onStatusChanged?: (novoStatus: boolean) => void;
  /** Estado de loading do reenvio */
  resending?: boolean;
  /** Link de ativação recém-gerado (resultado do resend) */
  newActivationLink?: string | null;
}

const STATUS_ICONS: Record<InviteStatusType, typeof CheckCircle2> = {
  ativo: CheckCircle2,
  ativando: Clock,
  pendente: Clock,
  expirado: AlertTriangle,
  suspenso: AlertTriangle,
};

export function InviteStatusCard({ user, onResend, onStatusChanged, resending, newActivationLink }: InviteStatusCardProps) {
  const [copied, setCopied] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [recoveryLink, setRecoveryLink] = useState<string | null>(null);
  const [ativoLocal, setAtivoLocal] = useState<boolean>(
    user.statusConta === 'ATIVA' || (user as any).ativo !== false
  );
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const [localRecMeta, setLocalRecMeta] = useState<any>(user.recuperacaoAcesso || null);

  // statusConta é autoritativo. O campo `ativo` (boolean) é soft-delete, não estado de ativação.
  // PENDENTE com ativo=true NÃO é ativo — o usuário nunca ativou a conta.
  const isAtivo = ativoLocal && user.statusConta === 'ATIVA';
  const info = isAtivo
    ? { status: 'ativo' as const, label: 'Ativo no Sistema', color: '#10b981', bgColor: 'rgba(16,185,129,.08)' }
    : getInviteStatus(user);
  const tempo = getTempoRestante(user.tokenExpiraEm);
  const StatusIcon = STATUS_ICONS[info.status] || Clock;

  const activeRecMeta = localRecMeta || user.recuperacaoAcesso || (recoveryLink ? {
    solicitadaEm: new Date().toISOString(),
    expiraEm: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
    solicitadaPorNome: 'Você',
    link: recoveryLink,
  } : null);

  const recTempo = getTempoRestante(activeRecMeta?.expiraEm);

  const activationLink = user.tokenAtivacao
    ? buildActivationLink(user.tokenAtivacao, user.codigoDisplay)
    : null;

  const handleCopy = async (link: string) => {
    const ok = await copyActivationLink(link);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleWhatsApp = () => {
    const targetLink = recoveryLink || activeRecMeta?.link || newActivationLink || activationLink;
    if (!targetLink) return;
    const url = buildWhatsAppUrl(user.nome, targetLink, user.perfil, user.telefone);
    window.open(url, '_blank');
  };

  const handleRegerar = async () => {
    if (!onResend) return;
    try {
      await onResend(user.id);
      setShowInviteDialog(true);
    } catch (e) {
      console.error('Erro ao regerar convite:', e);
    }
  };

  const handleRecuperarAcesso = async () => {
    setRecovering(true);
    try {
      const { data } = await api.post(`${ROUTES.admin.usuarios}/${user.id}/recovery-link`);
      if (data.activationLink) {
        setRecoveryLink(data.activationLink);
        if (data.recuperacaoAcesso) {
          setLocalRecMeta(data.recuperacaoAcesso);
        } else {
          setLocalRecMeta({
            solicitadaEm: new Date().toISOString(),
            expiraEm: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
            solicitadaPorNome: 'Você',
            link: data.activationLink,
          });
        }
        setShowInviteDialog(true);
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Não foi possível gerar a recuperação de acesso.';
      toast.error(msg, e);
    } finally {
      setRecovering(false);
    }
  };

  const handleToggleStatus = async (novoStatus: boolean) => {
    setTogglingStatus(true);
    try {
      await api.patch(`${ROUTES.admin.usuarios}/${user.id}/toggle`);
      setAtivoLocal(novoStatus);
      toast.success(novoStatus ? 'Usuário reativado com sucesso!' : 'Usuário desativado com sucesso! Agora você pode regerar um link de ativação.');
      onStatusChanged?.(novoStatus);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.response?.data?.error?.message || 'Erro ao alterar status do usuário', e);
    } finally {
      setTogglingStatus(false);
    }
  };

  const displayLink = recoveryLink || activeRecMeta?.link || newActivationLink || activationLink;

  return (
    <div
      className="invite-status-card"
      style={{ 
        background: 'var(--bg-card, #ffffff)', 
        border: '1px solid var(--border-color, #e5e7eb)',
        padding: '16px',
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        position: 'relative'
      }}
    >
      {/* Seção 1: Banner de Status */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        padding: '10px 14px',
        borderRadius: 8,
        background: info.bgColor,
        border: `1px solid ${info.color}25`,
        gap: 10
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <StatusIcon size={18} style={{ color: info.color, flexShrink: 0 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span className="invite-status-card__badge" style={{ color: info.color, fontSize: '0.82rem', fontWeight: 700 }}>
              {info.label}
            </span>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted, #6b7280)', fontWeight: 500 }}>
              {isAtivo 
                ? (user.ativadoEm ? `Ativado em ${formatDate(user.ativadoEm)}` : 'Acesso ativo e liberado')
                : (user.statusConta === 'PENDENTE' || info.status === 'expirado' || info.status === 'pendente')
                  ? `Convidado em: ${formatDate(user.criadoEm)} · Status: Link Expirado / Aguardando Ativação`
                  : `Status: Conta Suspensa / Desativada`
              }
            </span>
          </div>
        </div>

        {/* Balão Explicativo / Ícone de Interrogação */}
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setShowHelp(h => !h)}
            onMouseEnter={() => setShowHelp(true)}
            onMouseLeave={() => setShowHelp(false)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#94a3b8',
              display: 'flex',
              alignItems: 'center',
              padding: 4,
              borderRadius: '50%',
              transition: 'color .15s'
            }}
            title="Informações sobre o status do usuário"
          >
            <HelpCircle size={16} />
          </button>

          {showHelp && (
            <div style={{
              position: 'absolute',
              right: 0,
              bottom: '120%',
              width: 280,
              background: '#1e293b',
              color: '#ffffff',
              fontSize: '.72rem',
              lineHeight: 1.45,
              padding: '10px 12px',
              borderRadius: 8,
              boxShadow: '0 10px 25px rgba(0,0,0,0.25)',
              zIndex: 100,
              pointerEvents: 'none'
            }}>
              {isAtivo ? (
                <>
                  <strong>Usuário Ativo:</strong> Possui credenciais funcionais no sistema. Por segurança, links de primeiro acesso ficam desativados para contas ativas. Use <em>Recuperar Acesso</em> para emitir ou consultar links temporários de redefinição de senha, ou <em>Desativar Usuário</em> para emitir um novo link inicial de 72 horas.
                </>
              ) : (user.statusConta === 'PENDENTE' || info.status === 'expirado' || info.status === 'pendente') ? (
                <>
                  <strong>Link Expirado / Pendente:</strong> O usuário foi convidado mas ainda não ativou a conta. Clique em <em>Regerar Novo Link</em> para emitir um novo token de 72 horas e repassar ao membro.
                </>
              ) : (
                <>
                  <strong>Conta Suspensa:</strong> O acesso foi desativado temporariamente. Clique em <em>Reativar Usuário</em> para restaurar o acesso imediato.
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Seção 2: USUÁRIO ATIVO — Registro de Recuperação e Comandos */}
      {isAtivo ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Registro da Última Recuperação de Acesso Solicitada */}
          {activeRecMeta && (
            <div style={{
              background: 'var(--bg-muted, #f8fafc)',
              border: '1px solid var(--border-color, #e2e8f0)',
              borderRadius: 10,
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <KeyRound size={15} style={{ color: '#6366f1' }} />
                  <span style={{ fontSize: '.76rem', fontWeight: 700, color: 'var(--text-main, #1e293b)' }}>
                    Recuperação de Acesso Ativa
                  </span>
                </div>
                <span style={{
                  fontSize: '.68rem',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: 6,
                  background: recTempo.expirado ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                  color: recTempo.expirado ? '#dc2626' : '#047857',
                  border: `1px solid ${recTempo.expirado ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'}`,
                }}>
                  {recTempo.expirado ? '🔴 Link Expirado' : `🟢 Válido (restam ${recTempo.label})`}
                </span>
              </div>

              <div style={{ fontSize: '.72rem', color: 'var(--text-muted, #64748b)' }}>
                Solicitada em <strong>{formatDate(activeRecMeta.solicitadaEm)}</strong> por <strong>{activeRecMeta.solicitadaPorNome || 'Administrador'}</strong>
              </div>

              {activeRecMeta.link && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
                  <input
                    type="text"
                    readOnly
                    value={normalizeActivationLink(activeRecMeta.link)}
                    style={{
                      flex: '1 1 200px',
                      padding: '7px 10px',
                      fontSize: '0.72rem',
                      borderRadius: 8,
                      border: '1px solid var(--border-color, #cbd5e1)',
                      background: '#ffffff',
                      color: '#334155',
                      fontFamily: 'monospace',
                      outline: 'none',
                      minWidth: 0,
                    }}
                    onClick={() => void handleCopy(activeRecMeta.link)}
                    title="Clique para copiar"
                  />

                  <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap' }}>
                    <button
                      type="button"
                      className="invite-action-btn"
                      style={{
                        padding: '7px 10px',
                        borderRadius: 8,
                        background: copied ? 'rgba(16, 185, 129, 0.1)' : '#ffffff',
                        color: copied ? '#10b981' : '#475569',
                        border: '1px solid #cbd5e1',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                      onClick={() => handleCopy(activeRecMeta.link)}
                    >
                      {copied ? <Check size={13} /> : <Copy size={13} />}
                      {copied ? 'Copiado!' : 'Copiar'}
                    </button>

                    <button
                      type="button"
                      className="invite-action-btn"
                      style={{
                        padding: '7px 10px',
                        borderRadius: 8,
                        background: 'rgba(37, 211, 102, 0.08)',
                        color: '#25d366',
                        border: '1px solid rgba(37, 211, 102, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        const url = buildWhatsAppUrl(user.nome, activeRecMeta.link, user.perfil, user.telefone);
                        window.open(url, '_blank');
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.713-1.455L0 24zm6.59-4.846c1.6.95 3.167 1.45 4.882 1.451 5.4 0 9.794-4.392 9.797-9.799.002-2.618-1.01-5.08-2.852-6.924C16.634 2.04 14.178 1.026 12.01 1.026 6.613 1.026 2.22 5.419 2.218 10.824c0 1.777.466 3.513 1.35 5.068l-.98 3.582 3.668-.962zm11.378-7.14c-.08-.13-.292-.21-.614-.371-.321-.16-1.9-.938-2.193-1.045-.292-.11-.505-.16-.716.16-.21.32-.818 1.045-1.002 1.258-.184.21-.368.24-.69.08-.321-.16-1.358-.5-2.586-1.6-.955-.85-1.6-1.9-1.787-2.22-.185-.32-.02-.49.14-.65.145-.145.32-.37.48-.56.16-.18.21-.3.32-.5.11-.2.05-.37-.02-.53-.08-.16-.716-1.73-.98-2.37-.258-.62-.52-.53-.716-.54-.184-.01-.395-.01-.607-.01-.21 0-.553.08-.843.4-.29.32-1.108 1.08-1.108 2.63 0 1.55 1.13 3.05 1.282 3.26.158.21 2.213 3.38 5.361 4.74.75.32 1.332.52 1.787.66.753.24 1.438.2 1.98.12.603-.09 1.9-.77 2.167-1.48.267-.71.267-1.32.187-1.45z"/>
                      </svg>
                      WhatsApp
                    </button>

                    <button
                      type="button"
                      className="invite-action-btn"
                      style={{
                        padding: '7px 10px',
                        borderRadius: 8,
                        background: 'rgba(99, 102, 241, 0.08)',
                        color: '#6366f1',
                        border: '1px solid rgba(99, 102, 241, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        setRecoveryLink(activeRecMeta.link);
                        setShowInviteDialog(true);
                      }}
                    >
                      <Share2 size={13} />
                      QR Code
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Botões de Ação para Usuário Ativo */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <button
              type="button"
              className="btn-primary"
              onClick={handleRecuperarAcesso}
              disabled={recovering}
              style={{
                padding: '9px 14px',
                fontSize: '.78rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                fontWeight: 700
              }}
            >
              <RefreshCw size={14} className={recovering ? 'animate-spin' : ''} />
              {recovering ? 'Gerando...' : (activeRecMeta ? 'Gerar Nova Recuperação' : 'Recuperar Acesso')}
            </button>

            <button
              type="button"
              onClick={() => handleToggleStatus(false)}
              disabled={togglingStatus}
              style={{
                padding: '9px 14px',
                fontSize: '.78rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                fontWeight: 700,
                borderRadius: 8,
                border: '1px solid rgba(239, 68, 68, 0.3)',
                background: 'rgba(239, 68, 68, 0.08)',
                color: '#dc2626',
                cursor: 'pointer',
                transition: 'all .15s'
              }}
            >
              <UserX size={14} />
              {togglingStatus ? 'Desativando...' : 'Desativar Usuário'}
            </button>
          </div>
        </div>
      ) : (
        /* Seção 3: USUÁRIO INATIVO / EXPIRADO — Link de Ativação e Ações */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {displayLink && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Link de ativação atual
              </label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input
                  type="text"
                  readOnly
                  value={normalizeActivationLink(displayLink)}
                  style={{
                    flex: '1 1 240px',
                    padding: '8px 12px',
                    fontSize: '0.75rem',
                    borderRadius: 8,
                    border: '1px solid var(--border-color, #cbd5e1)',
                    background: 'var(--bg-muted, #f8fafc)',
                    color: 'var(--text-main, #334155)',
                    fontFamily: 'monospace',
                    outline: 'none',
                    minWidth: 0
                  }}
                  onClick={() => {
                    void handleCopy(displayLink);
                  }}
                  title="Clique para copiar"
                />

                <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap' }}>
                  <button
                    type="button"
                    className="invite-action-btn"
                    style={{ 
                      padding: '8px 12px', 
                      borderRadius: 8, 
                      background: copied ? 'rgba(16, 185, 129, 0.1)' : 'var(--bg-card, #ffffff)', 
                      color: copied ? '#10b981' : 'var(--text-main, #475569)', 
                      border: '1px solid var(--border-color, #cbd5e1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                    onClick={() => handleCopy(displayLink)}
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? 'Copiado!' : 'Copiar'}
                  </button>

                  <button 
                    type="button"
                    className="invite-action-btn" 
                    style={{ 
                      padding: '8px 12px', 
                      borderRadius: 8, 
                      background: 'rgba(37, 211, 102, 0.08)', 
                      color: '#25d366', 
                      border: '1px solid rgba(37, 211, 102, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                    onClick={handleWhatsApp}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.713-1.455L0 24zm6.59-4.846c1.6.95 3.167 1.45 4.882 1.451 5.4 0 9.794-4.392 9.797-9.799.002-2.618-1.01-5.08-2.852-6.924C16.634 2.04 14.178 1.026 12.01 1.026 6.613 1.026 2.22 5.419 2.218 10.824c0 1.777.466 3.513 1.35 5.068l-.98 3.582 3.668-.962zm11.378-7.14c-.08-.13-.292-.21-.614-.371-.321-.16-1.9-.938-2.193-1.045-.292-.11-.505-.16-.716.16-.21.32-.818 1.045-1.002 1.258-.184.21-.368.24-.69.08-.321-.16-1.358-.5-2.586-1.6-.955-.85-1.6-1.9-1.787-2.22-.185-.32-.02-.49.14-.65.145-.145.32-.37.48-.56.16-.18.21-.3.32-.5.11-.2.05-.37-.02-.53-.08-.16-.716-1.73-.98-2.37-.258-.62-.52-.53-.716-.54-.184-.01-.395-.01-.607-.01-.21 0-.553.08-.843.4-.29.32-1.108 1.08-1.108 2.63 0 1.55 1.13 3.05 1.282 3.26.158.21 2.213 3.38 5.361 4.74.75.32 1.332.52 1.787.66.753.24 1.438.2 1.98.12.603-.09 1.9-.77 2.167-1.48.267-.71.267-1.32.187-1.45z"/>
                    </svg>
                    WhatsApp
                  </button>

                  <button 
                    type="button"
                    className="invite-action-btn" 
                    style={{ 
                      padding: '8px 12px', 
                      borderRadius: 8, 
                      background: 'rgba(99, 102, 241, 0.08)', 
                      color: '#6366f1', 
                      border: '1px solid rgba(99, 102, 241, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                    onClick={() => setShowInviteDialog(true)}
                  >
                    <Share2 size={14} />
                    Compartilhar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Botões de Ação para Conta Inativa */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, borderTop: '1px dashed var(--border-color, #e5e7eb)', paddingTop: 12 }}>
            {/* Se o convite está pendente ou expirado, a ação correta é regerar o link de 72h */}
            {(user.statusConta === 'PENDENTE' || info.status === 'expirado' || info.status === 'pendente') ? (
              onResend && (
                <button
                  type="button"
                  className="invite-action-btn"
                  style={{ 
                    padding: '10px 14px', 
                    borderRadius: 8, 
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    color: '#ffffff', 
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow: '0 4px 10px rgba(245, 158, 11, 0.15)',
                    transition: 'all 0.2s',
                  }}
                  onClick={handleRegerar}
                  disabled={resending}
                  title="Regerar um novo link de ativação de 72 horas"
                >
                  <RefreshCw
                    size={14}
                    className={resending ? 'animate-spin' : ''}
                  />
                  {resending ? 'Regerando...' : 'Regerar Novo Link de Ativação'}
                </button>
              )
            ) : (
              /* Se a conta foi previamente ativada mas está suspensa/bloqueada, a ação é reativar */
              <button
                type="button"
                onClick={() => handleToggleStatus(true)}
                disabled={togglingStatus}
                style={{
                  padding: '10px 14px',
                  fontSize: '.78rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  fontWeight: 700,
                  borderRadius: 8,
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  background: 'rgba(16, 185, 129, 0.08)',
                  color: '#047857',
                  cursor: 'pointer',
                  transition: 'all .15s'
                }}
              >
                <UserCheck size={14} />
                {togglingStatus ? 'Reativando...' : 'Reativar Usuário'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Modal / Dialog com QR Code e compartilhamento unificado */}
      {showInviteDialog && displayLink && (
        <InviteResultDialog
          open={true}
          nome={user.nome}
          email={user.email}
          perfil={user.perfil}
          telefone={user.telefone}
          activationLink={displayLink}
          kind={recoveryLink ? 'recuperacao' : 'ativacao'}
          onClose={() => {
            setShowInviteDialog(false);
            setRecoveryLink(null);
          }}
        />
      )}
    </div>
  );
}
